import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, Circle, Sparkles, Trophy, Clock, BookOpen,
  Calculator, Pencil, FlaskConical, Heart, Globe, Shield,
  Loader2, ChevronRight, Star, Zap, Play
} from 'lucide-react';

const moduleIcons = {
  reading: BookOpen,
  math: Calculator,
  writing: Pencil,
  science: FlaskConical,
  bible: Heart,
  spanish: Globe,
  french: Globe,
  akan: Globe,
  digital_safety: Shield
};

const moduleColors = {
  reading: 'bg-teal-100 text-teal-600',
  math: 'bg-red-100 text-red-600',
  writing: 'bg-cyan-100 text-cyan-600',
  science: 'bg-purple-100 text-purple-600',
  bible: 'bg-pink-100 text-pink-600',
  spanish: 'bg-amber-100 text-amber-600',
  french: 'bg-blue-100 text-blue-600',
  akan: 'bg-orange-100 text-orange-600',
  digital_safety: 'bg-slate-100 text-slate-600'
};

const DailyTasksWidget = ({ childId, compact = false, onTaskComplete }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ completed: 0, total: 0, progress_percent: 0, xp_earned: 0 });
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState({});
  const [generatingTasks, setGeneratingTasks] = useState(false);

  useEffect(() => {
    if (childId) {
      fetchTasks();
    }
  }, [childId]);

  const fetchTasks = async () => {
    try {
      const response = await api.get(`/children/${childId}/daily-tasks`);
      setTasks(response.data.tasks || []);
      setStats(response.data.stats || { completed: 0, total: 0, progress_percent: 0, xp_earned: 0 });
    } catch (error) {
      console.error('Failed to fetch daily tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAITasks = async () => {
    setGeneratingTasks(true);
    try {
      const response = await api.post(`/daily-tasks/generate/${childId}`);
      toast.success(response.data.message || 'AI tasks generated!', {
        icon: <Sparkles className="w-5 h-5 text-purple-500" />
      });
      // Refresh tasks
      await fetchTasks();
    } catch (error) {
      console.error('Failed to generate AI tasks:', error);
      toast.error('Failed to generate tasks. Please try again.');
    } finally {
      setGeneratingTasks(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    setCompleting(prev => ({ ...prev, [taskId]: true }));
    try {
      const response = await api.post(`/daily-tasks/${taskId}/complete`);
      
      if (response.data.success) {
        toast.success(`+${response.data.xp_earned} XP earned!`, {
          icon: <Zap className="w-5 h-5 text-amber-500" />
        });
        
        // Update local state
        setTasks(prev => prev.map(t => 
          t.id === taskId ? { ...t, completed: true } : t
        ));
        setStats(prev => ({
          ...prev,
          completed: prev.completed + 1,
          progress_percent: Math.round(((prev.completed + 1) / prev.total) * 100),
          xp_earned: prev.xp_earned + response.data.xp_earned
        }));
        
        if (response.data.all_completed) {
          toast.success('🎉 All daily tasks completed! Amazing job!', {
            duration: 5000
          });
        }
        
        if (onTaskComplete) {
          onTaskComplete(response.data);
        }
      }
    } catch (error) {
      toast.error('Failed to complete task');
    } finally {
      setCompleting(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Navigate to the task's learning activity
  const handleGoToTask = (task) => {
    if (task.completed) {
      toast.info('This task is already completed!');
      return;
    }
    
    // Store the task ID so we can auto-complete it when done
    localStorage.setItem('activeTaskId', task.id);
    localStorage.setItem('activeTaskModule', task.module);
    
    // Determine the navigation path based on module
    const modulePaths = {
      reading: `/adaptive/${childId}/reading`,
      math: `/adaptive/${childId}/math`,
      writing: `/adaptive/${childId}/writing`,
      science: `/adaptive/${childId}/science`,
      bible: `/bible-stories/${childId}`,
      spanish: `/language/${childId}/spanish`,
      french: `/language/${childId}/french`,
      akan: `/language/${childId}/akan`,
      digital_safety: `/digital-safety/${childId}`
    };
    
    const targetPath = modulePaths[task.module] || `/learn/${childId}`;
    
    toast.info(`Starting: ${task.task_title.replace(/^[^\s]+\s/, '')}`, {
      icon: <Play className="w-4 h-4 text-teal-500" />
    });
    
    navigate(targetPath);
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="w-full bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Daily Tasks</h3>
                <p className="text-xs text-slate-500">{stats.completed}/{stats.total} completed</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-amber-600">+{stats.xp_earned}</span>
              <span className="text-xs text-slate-500 block">XP</span>
            </div>
          </div>
          <Progress value={stats.progress_percent} className="h-2 bg-teal-100" />
          {stats.progress_percent === 100 && (
            <div className="mt-2 text-center">
              <span className="text-xs text-teal-600 font-medium flex items-center justify-center gap-1">
                <Trophy className="w-3 h-3" /> All complete!
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full" data-testid="daily-tasks-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Today's Learning Tasks</CardTitle>
              <p className="text-sm text-slate-500">Complete tasks to earn XP!</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-amber-600">
              <Star className="w-5 h-5 fill-amber-400" />
              <span className="text-xl font-bold">{stats.xp_earned}</span>
            </div>
            <span className="text-xs text-slate-500">XP earned today</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">{stats.completed} of {stats.total} tasks</span>
            <span className="font-medium text-teal-600">{stats.progress_percent}%</span>
          </div>
          <Progress value={stats.progress_percent} className="h-3 bg-slate-100" />
        </div>

        {/* Completion Banner */}
        {stats.progress_percent === 100 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl text-white text-center"
          >
            <div className="flex items-center justify-center gap-2">
              <Trophy className="w-5 h-5" />
              <span className="font-bold">All Tasks Complete!</span>
              <Trophy className="w-5 h-5" />
            </div>
            <p className="text-sm opacity-90 mt-1">Great job today! Come back tomorrow for more.</p>
          </motion.div>
        )}

        {/* Tasks List */}
        <div className="space-y-2">
          <AnimatePresence>
            {tasks.map((task, index) => {
              const IconComponent = moduleIcons[task.module] || BookOpen;
              const colorClass = moduleColors[task.module] || 'bg-slate-100 text-slate-600';
              
              return (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => !task.completed && handleGoToTask(task)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                    task.completed 
                      ? 'bg-teal-50 border border-teal-200' 
                      : 'bg-white border border-slate-200 hover:border-teal-300 hover:shadow-md cursor-pointer group'
                  }`}
                  data-testid={`daily-task-${task.id}`}
                >
                  {/* Module Icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass} ${!task.completed && 'group-hover:scale-110 transition-transform'}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  
                  {/* Task Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium text-sm ${task.completed ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                      {task.task_title}
                    </h4>
                    <p className="text-xs text-slate-500 truncate">{task.task_description}</p>
                    {!task.completed && (
                      <p className="text-xs text-teal-600 mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="w-3 h-3" /> Click to start
                      </p>
                    )}
                  </div>
                  
                  {/* XP Badge */}
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    task.completed 
                      ? 'bg-teal-100 text-teal-600' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    +{task.xp_reward} XP
                  </div>
                  
                  {/* Complete Status / Go Button */}
                  {task.completed ? (
                    <CheckCircle2 className="w-6 h-6 text-teal-500 flex-shrink-0" />
                  ) : (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {tasks.length === 0 && (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">No tasks for today yet</p>
            <p className="text-sm text-slate-400 mb-4">Generate personalized AI tasks based on your learning progress!</p>
            <Button
              onClick={generateAITasks}
              disabled={generatingTasks}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              data-testid="generate-ai-tasks-btn"
            >
              {generatingTasks ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate AI Tasks
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyTasksWidget;
